/**
 * Viewer-side binary cache for model files (frag / glb / gltf).
 *
 * Why IndexedDB instead of localStorage:
 *   - localStorage is ~5-10 MB per origin, requires base64 (×1.33 size),
 *     and synchronous APIs block the main thread for big buffers.
 *   - IndexedDB stores ArrayBuffer natively, has GBs of quota, and is
 *     async-friendly. Perfect for re-using model downloads across viewer
 *     reloads without re-fetching from R2 every time.
 *
 * Why not the Cache API:
 *   - Cache API keys by Request URL, but our R2 URLs are short-lived
 *     pre-signed and change every load — so URL-keyed caches would never
 *     hit. We key by stable `fileId`/`modelId` instead.
 *
 * Storage shape:
 *   DB     : `echov-viewer-cache`
 *   Store  : `models`
 *   Key    : `<userId>:<fileIdOrModelId>`
 *   Value  : { buffer, modelType?, etag?, size, name?, timestamp }
 *
 * We don't try to be clever with eviction yet — IndexedDB will surface
 * QuotaExceededError on write and we fall back to a plain fetch.
 */

const DB_NAME = "echov-viewer-cache";
const STORE = "models";
const DB_VERSION = 1;

export interface ViewerCacheEntry {
  buffer: ArrayBuffer;
  size: number;
  timestamp: number;
  modelType?: string;
  etag?: string;
  name?: string;
}

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB not available"));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
    req.onblocked = () =>
      console.warn("[viewerModelCache] open blocked — close other tabs?");
  });
  return dbPromise;
}

async function tx<T>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest<T> | T
): Promise<T> {
  const db = await openDb();
  return new Promise<T>((resolve, reject) => {
    const t = db.transaction(STORE, mode);
    const store = t.objectStore(STORE);
    const result = fn(store);
    if (result && typeof (result as IDBRequest).addEventListener === "function") {
      (result as IDBRequest<T>).onsuccess = () =>
        resolve((result as IDBRequest<T>).result);
      (result as IDBRequest<T>).onerror = () =>
        reject((result as IDBRequest<T>).error);
    } else {
      t.oncomplete = () => resolve(result as T);
      t.onerror = () => reject(t.error);
    }
  });
}

function buildKey(userId: string, id: string): string {
  return `${userId || "anon"}:${id}`;
}

/** Read a cached entry (or null on miss / error). */
export async function viewerCacheGet(
  userId: string,
  id: string
): Promise<ViewerCacheEntry | null> {
  try {
    const v = await tx<ViewerCacheEntry | undefined>("readonly", (store) =>
      store.get(buildKey(userId, id))
    );
    return v || null;
  } catch (err) {
    console.warn("[viewerModelCache] get failed", err);
    return null;
  }
}

/** Persist an entry. Returns true if saved, false on quota / error. */
export async function viewerCacheSet(
  userId: string,
  id: string,
  buffer: ArrayBuffer,
  meta?: { modelType?: string; etag?: string; name?: string }
): Promise<boolean> {
  try {
    const entry: ViewerCacheEntry = {
      buffer,
      size: buffer.byteLength,
      timestamp: Date.now(),
      modelType: meta?.modelType,
      etag: meta?.etag,
      name: meta?.name,
    };
    await tx("readwrite", (store) => store.put(entry, buildKey(userId, id)));
    return true;
  } catch (err) {
    console.warn("[viewerModelCache] set failed (will continue without cache)", err);
    return false;
  }
}

/** Delete a single entry. */
export async function viewerCacheDelete(userId: string, id: string): Promise<void> {
  try {
    await tx("readwrite", (store) => store.delete(buildKey(userId, id)));
  } catch (err) {
    console.warn("[viewerModelCache] delete failed", err);
  }
}

/**
 * Wipe everything in the store. Useful for a "Clear viewer cache" admin
 * action.
 */
export async function viewerCacheClearAll(): Promise<void> {
  try {
    await tx("readwrite", (store) => store.clear());
    console.log("[viewerModelCache] cleared all entries");
  } catch (err) {
    console.warn("[viewerModelCache] clearAll failed", err);
  }
}

/** Sum of all cached buffer sizes (in bytes). */
export async function viewerCacheTotalSize(): Promise<number> {
  try {
    const db = await openDb();
    return await new Promise<number>((resolve, reject) => {
      let total = 0;
      const t = db.transaction(STORE, "readonly");
      const store = t.objectStore(STORE);
      const cursorReq = store.openCursor();
      cursorReq.onsuccess = () => {
        const cursor = cursorReq.result;
        if (cursor) {
          const v = cursor.value as ViewerCacheEntry;
          total += v?.size || 0;
          cursor.continue();
        } else {
          resolve(total);
        }
      };
      cursorReq.onerror = () => reject(cursorReq.error);
    });
  } catch (err) {
    console.warn("[viewerModelCache] totalSize failed", err);
    return 0;
  }
}

/**
 * High-level helper: try cache first, then fetch and cache.
 *
 * `cacheKey` should be stable across signed-URL refreshes — typically the
 * MongoDB fileId or, fallback, the modelId.
 */
export async function viewerCacheFetch(opts: {
  userId: string;
  cacheKey: string;
  url: string;
  modelType?: string;
  name?: string;
}): Promise<{ buffer: ArrayBuffer; cached: boolean }> {
  const t0 = performance.now();
  const hit = await viewerCacheGet(opts.userId, opts.cacheKey);
  if (hit && hit.buffer && hit.buffer.byteLength > 0) {
    console.log(
      `💾 [viewerCache] HIT ${opts.name || opts.cacheKey} ` +
        `(${(hit.buffer.byteLength / 1024 / 1024).toFixed(2)} MB, ${(performance.now() - t0).toFixed(0)}ms)`
    );
    return { buffer: hit.buffer, cached: true };
  }
  const r = await fetch(opts.url);
  if (!r.ok) {
    throw new Error(`HTTP ${r.status} ${r.statusText} fetching ${opts.name || opts.cacheKey}`);
  }
  const buffer = await r.arrayBuffer();
  console.log(
    `🌐 [viewerCache] MISS ${opts.name || opts.cacheKey} ` +
      `(${(buffer.byteLength / 1024 / 1024).toFixed(2)} MB downloaded, ${(performance.now() - t0).toFixed(0)}ms)`
  );
  // Best-effort cache; never let a write failure block the load.
  void viewerCacheSet(opts.userId, opts.cacheKey, buffer, {
    modelType: opts.modelType,
    name: opts.name,
    etag: r.headers.get("etag") || undefined,
  });
  return { buffer, cached: false };
}
